import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

export type CanonicalTreeDeletionErrorClass =
  | 'ACCIDENTAL_DUPLICATE'
  | 'WRONG_PERSON'
  | 'EMPTY_PLACEHOLDER'
  | 'TEST_RECORD'
  | 'FAILED_CREATION';

export type CanonicalTreeDeletionBlocker =
  | 'MEMBER_NOT_FOUND'
  | 'ERROR_CLASS_REQUIRED'
  | 'CLAIMED_PROFILE'
  | 'VIEWER_ANCHOR'
  | 'HAS_CANONICAL_PARENTS'
  | 'HAS_CANONICAL_CHILDREN'
  | 'HAS_SPOUSE_REFERENCE'
  | 'HAS_MEDIA'
  | 'HAS_MEMORIES'
  | 'HAS_INVITATIONS'
  | 'HAS_CLAIM_HISTORY'
  | 'HAS_EXTERNAL_DEPENDENCIES';

export interface CanonicalTreeDeletionDependencyEvidence {
  readonly mediaCount: number;
  readonly memoryCount: number;
  readonly invitationCount: number;
  readonly claimHistoryCount: number;
  readonly externalDependencyCount: number;
}

export interface CanonicalTreeErrorOnlyDeletionInput {
  readonly members:
    readonly GraphMember[];
  readonly memberId: string;
  readonly viewerMemberId: string | null;
  readonly errorClass:
    CanonicalTreeDeletionErrorClass | null;
  readonly dependencies:
    CanonicalTreeDeletionDependencyEvidence;
}

export interface CanonicalTreeErrorOnlyDeletionDecision {
  readonly authorized: boolean;
  readonly memberId: string;
  readonly errorClass:
    CanonicalTreeDeletionErrorClass | null;
  readonly blockers:
    readonly CanonicalTreeDeletionBlocker[];
  readonly canonicalParentIds:
    readonly string[];
  readonly canonicalChildIds:
    readonly string[];
  readonly spouseReferenceIds:
    readonly string[];
  readonly preservesOtherMembers: boolean;
  readonly preservesProfileEditing: true;
  readonly reason: string;
}

const normalizeCount = (
  value: number,
): number =>
  Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;

const uniqueIds = (
  values: readonly string[],
): string[] =>
  Array.from(
    new Set(
      values.filter(Boolean),
    ),
  ).sort();

const activeMembers = (
  members: readonly GraphMember[],
): readonly GraphMember[] =>
  members.filter(
    member =>
      member.approvalStatus !== 'rejected' &&
      member.profileState !== 'deleted',
  );

export const authorizeCanonicalTreeErrorOnlyDeletion = (
  input:
    CanonicalTreeErrorOnlyDeletionInput,
): CanonicalTreeErrorOnlyDeletionDecision => {
  const active =
    activeMembers(input.members);

  const member =
    active.find(
      candidate =>
        candidate.id === input.memberId,
    );

  if (!member) {
    return Object.freeze({
      authorized: false,
      memberId:
        input.memberId,
      errorClass:
        input.errorClass,
      blockers:
        Object.freeze([
          'MEMBER_NOT_FOUND',
        ]),
      canonicalParentIds:
        Object.freeze([]),
      canonicalChildIds:
        Object.freeze([]),
      spouseReferenceIds:
        Object.freeze([]),
      preservesOtherMembers:
        false,
      preservesProfileEditing:
        true,
      reason:
        'The selected active family record was not found.',
    });
  }

  const activeIds =
    new Set(
      active.map(
        candidate =>
          candidate.id,
      ),
    );

  const canonicalParentIds =
    uniqueIds(
      (member.parentIds ?? []).filter(
        parentId =>
          activeIds.has(parentId),
      ),
    );

  const canonicalChildIds =
    uniqueIds(
      active
        .filter(
          candidate =>
            (
              candidate.parentIds ??
              []
            ).includes(member.id),
        )
        .map(
          candidate =>
            candidate.id,
        ),
    );

  const spouseReferenceIds =
    uniqueIds([
      ...(
        member.spouseId &&
        activeIds.has(member.spouseId)
          ? [member.spouseId]
          : []
      ),
      ...active
        .filter(
          candidate =>
            candidate.spouseId === member.id,
        )
        .map(
          candidate =>
            candidate.id,
        ),
    ]);

  const blockers:
    CanonicalTreeDeletionBlocker[] = [];

  if (!input.errorClass) {
    blockers.push(
      'ERROR_CLASS_REQUIRED',
    );
  }

  if (
    member.profileState === 'claimed' ||
    Boolean(member.claimedByUserId)
  ) {
    blockers.push(
      'CLAIMED_PROFILE',
    );
  }

  if (
    input.viewerMemberId === member.id
  ) {
    blockers.push(
      'VIEWER_ANCHOR',
    );
  }

  if (
    canonicalParentIds.length > 0
  ) {
    blockers.push(
      'HAS_CANONICAL_PARENTS',
    );
  }

  if (
    canonicalChildIds.length > 0
  ) {
    blockers.push(
      'HAS_CANONICAL_CHILDREN',
    );
  }

  if (
    spouseReferenceIds.length > 0
  ) {
    blockers.push(
      'HAS_SPOUSE_REFERENCE',
    );
  }

  if (
    normalizeCount(
      input.dependencies.mediaCount,
    ) > 0
  ) {
    blockers.push(
      'HAS_MEDIA',
    );
  }

  if (
    normalizeCount(
      input.dependencies.memoryCount,
    ) > 0
  ) {
    blockers.push(
      'HAS_MEMORIES',
    );
  }

  if (
    normalizeCount(
      input.dependencies.invitationCount,
    ) > 0
  ) {
    blockers.push(
      'HAS_INVITATIONS',
    );
  }

  if (
    normalizeCount(
      input.dependencies.claimHistoryCount,
    ) > 0
  ) {
    blockers.push(
      'HAS_CLAIM_HISTORY',
    );
  }

  if (
    normalizeCount(
      input.dependencies
        .externalDependencyCount,
    ) > 0
  ) {
    blockers.push(
      'HAS_EXTERNAL_DEPENDENCIES',
    );
  }

  const authorized =
    blockers.length === 0;

  return Object.freeze({
    authorized,
    memberId:
      member.id,
    errorClass:
      input.errorClass,
    blockers:
      Object.freeze(blockers),
    canonicalParentIds:
      Object.freeze(
        canonicalParentIds,
      ),
    canonicalChildIds:
      Object.freeze(
        canonicalChildIds,
      ),
    spouseReferenceIds:
      Object.freeze(
        spouseReferenceIds,
      ),
    preservesOtherMembers:
      authorized,
    preservesProfileEditing:
      true,
    reason:
      authorized
        ? 'The record is explicitly classified as an error and has no surviving family-history dependencies.'
        : 'Deletion is prohibited. Use Edit Profile, Correct Relationship, or governed duplicate merge.',
  });
};
